import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit, Trash, Search, Filter, Image, X, Upload, Eye, EyeOff, Users, RefreshCw, Shield, UserCheck } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authUtils from '../utils/authUtils';

const DSNVManagement = () => {
    // State Management
    const [employees, setEmployees] = useState([]);
    const [currentEmployee, setCurrentEmployee] = useState({
        'Họ và Tên': '',
        'Chức vụ': '',
        'Phòng': '',
        'username': '',
        'password': '',
        'Phân quyền': 'User', // Default value
        'Quyền xem': '', // Comma-separated menu paths
        'Email': '',
        'Image': ''
    });
    const [showModal, setShowModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [search, setSearch] = useState('');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [employeeToDelete, setEmployeeToDelete] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [isEditMode, setIsEditMode] = useState(false);
    const [filterPosition, setFilterPosition] = useState('TẤT CẢ');
    const [filterDepartment, setFilterDepartment] = useState('TẤT CẢ');
    const [selectedImage, setSelectedImage] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    // Add a ref for the drop zone
    const dropZoneRef = useRef(null);
    // Add drag state to show visual feedback
    const [isDragging, setIsDragging] = useState(false);
    // State for menu permissions
    const [selectedMenuPaths, setSelectedMenuPaths] = useState([]);

    // Danh sách tất cả các menu items có thể chọn
    const allMenuItems = [
        // Quản lý kho
        { path: '/giaban', title: 'Cài đặt giá bán', category: 'Quản lý kho' },
        { path: '/dmhh', title: 'Quản lý hàng hóa', category: 'Quản lý kho' },
        { path: '/xuatnhapkho', title: 'Quản lý xuất nhập kho', category: 'Quản lý kho' },
        { path: '/chitietxuatnhapkho', title: 'Chi tiết xuất nhập kho', category: 'Quản lý kho' },
        { path: '/tonkho', title: 'Báo cáo kho', category: 'Quản lý kho' },
        { path: '/makien', title: 'Mã kiện', category: 'Quản lý kho' },
        { path: '/duyet_sua_phieu', title: 'Duyệt yêu cầu', category: 'Quản lý kho' },
        // Quản lý năng suất
        { path: '/congdoan_dongia', title: 'Công đoạn - Đơn giá', category: 'Quản lý năng suất' },
        { path: '/phanbonhansu', title: 'Phân bổ nhân sự', category: 'Quản lý năng suất' },
        { path: '/nhapbaobi', title: 'Nhập - Phân bổ bao bì', category: 'Quản lý năng suất' },
        { path: '/chitiet_phanbo', title: 'Chi tiết phân bổ', category: 'Quản lý năng suất' },
        { path: '/baocao_nangsuat', title: 'Tổng hợp năng suất', category: 'Quản lý năng suất' },
        // Quản lý công lương
        { path: '/bangcongluong', title: 'Bảng công lương', category: 'Quản lý công lương' },
        { path: '/cauhinh_congluong', title: 'Cấu hình công lương', category: 'Quản lý công lương' },
        { path: '/maubangluong', title: 'Mẫu bảng lương', category: 'Quản lý công lương' },
        { path: '/thoathuan_congluong', title: 'Thỏa thuận lương', category: 'Quản lý công lương' },
        { path: '/ungluong', title: 'Phiếu ứng lương', category: 'Quản lý công lương' },
        { path: '/lichngayle', title: 'Lịch ngày lễ', category: 'Quản lý công lương' },
        { path: '/phepnam', title: 'Phép năm', category: 'Quản lý công lương' },
        { path: '/sotiengiulai', title: 'Sổ tiền giữ lại', category: 'Quản lý công lương' },
        { path: '/baocao_congluong', title: 'Báo cáo công lương', category: 'Quản lý công lương' },
        // Quản lý bán hàng
        { path: '/dskh', title: 'Danh sách khách hàng', category: 'Quản lý bán hàng' },
        { path: '/hopdongnguyentac', title: 'Hợp đồng nguyên tắc', category: 'Quản lý bán hàng' },
        { path: '/baocaobanhang', title: 'Báo cáo bán hàng', category: 'Quản lý bán hàng' },
        // Quản lý lô
        { path: '/quanlylo', title: 'Quản lý lô', category: 'Quản lý lô' },
        { path: '/thuchi', title: 'Quản lý thu chi theo lô', category: 'Quản lý lô' },
        // Chức năng khác
        { path: '/users', title: 'Quản lý người dùng', category: 'Chức năng khác' },
        { path: '/nhansu', title: 'Quản lý nhân sự', category: 'Chức năng khác' },
        { path: '/huongdansudung', title: 'Hướng dẫn sử dụng', category: 'Chức năng khác' }
    ];

    useEffect(() => {
        const checkAdminPermission = () => {
            const userData = authUtils.getUserData();
            const isAdminUser = userData && userData['Phân quyền'] === 'Admin';
            setIsAdmin(isAdminUser);
        };

        checkAdminPermission();
        fetchEmployees();
    }, []);
    // Fetch data
    const fetchEmployees = async () => {
        try {
            const response = await authUtils.apiRequest('DSNV', 'Find', {});
            setEmployees(response);
        } catch (error) {
            console.error('Error fetching employee list:', error);
            toast.error('Lỗi khi tải danh sách nhân viên');
        }
    };

    useEffect(() => {
        fetchEmployees();
    }, []);

    // Image upload handler
    const handleImageUpload = async (file) => {
        if (!file) return;

        try {
            // Show preview for better UX
            const reader = new FileReader();
            reader.onloadend = () => {
                setSelectedImage(reader.result);
            };
            reader.readAsDataURL(file);

            // Show loading toast
            const toastId = toast.loading("Đang tải ảnh lên...");

            // Call API to upload image
            const result = await authUtils.uploadImage(file);

            if (result && result.success && result.url) {
                // Update image URL from upload result
                handleInputChange('Image', result.url);
                setSelectedImage(result.url);
                toast.update(toastId, {
                    render: "Tải ảnh thành công",
                    type: "success",
                    isLoading: false,
                    autoClose: 2000
                });
            } else {
                throw new Error("Không thể lấy URL ảnh");
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error('Lỗi khi tải ảnh: ' + error.message);
            // If upload fails, remove preview
            setSelectedImage(null);
        }
    };

    // Drag and drop handlers
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];

            // Validate file before proceeding
            const validation = authUtils.validateImage(file);

            if (!validation.isValid) {
                toast.error(validation.errors.join('\n'));
                return;
            }

            // Handle the dropped file
            setCurrentEmployee(prev => ({
                ...prev,
                'FILE_IMAGE': file
            }));

            // Show preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setSelectedImage(reader.result);
            };
            reader.readAsDataURL(file);

            // Upload the image right away if needed
            if (authUtils.shouldUploadImmediately) {
                handleImageUpload(file);
            }
        }
    };

    // Modal handlers
    const handleOpenModal = (employee = null) => {
        // Nếu không phải admin thì không cho phép mở modal
        if (!isAdmin) {
            toast.error('Bạn không có quyền thực hiện chức năng này!');
            return;
        }

        if (employee) {
            setIsEditMode(true);
            setCurrentEmployee({
                'Họ và Tên': employee['Họ và Tên'] || '',
                'Chức vụ': employee['Chức vụ'] || '',
                'Phòng': employee['Phòng'] || '',
                'username': employee['username'] || '',
                'password': employee['password'] || '',
                'Phân quyền': employee['Phân quyền'] || 'User',
                'Quyền xem': employee['Quyền xem'] || '',
                'Email': employee['Email'] || '',
                'Image': employee['Image'] || ''
            });
            setSelectedImage(employee['Image'] || null);
            // Parse Quyền xem thành array
            const permissions = employee['Quyền xem'] ? employee['Quyền xem'].split(',').filter(p => p.trim()) : [];
            setSelectedMenuPaths(permissions);
        } else {
            setIsEditMode(false);
            setCurrentEmployee({
                'Họ và Tên': '',
                'Chức vụ': '',
                'Phòng': '',
                'username': '',
                'password': '',
                'Phân quyền': 'User',
                'Quyền xem': '',
                'Email': '',
                'Image': ''
            });
            setSelectedImage(null);
            setSelectedMenuPaths([]);
        }
        setShowPassword(false);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setIsEditMode(false);
        setSelectedImage(null);
        setSelectedMenuPaths([]);
        setCurrentEmployee({
            'Họ và Tên': '',
            'Chức vụ': '',
            'Phòng': '',
            'username': '',
            'password': '',
            'Phân quyền': 'User',
            'Quyền xem': '',
            'Email': '',
            'Image': ''
        });
    };

    // Form handlers
    const handleInputChange = (field, value) => {
        setCurrentEmployee(prev => ({
            ...prev,
            [field]: value
        }));
        // Nếu thay đổi Phân quyền sang Admin, reset quyền xem
        if (field === 'Phân quyền' && value === 'Admin') {
            setSelectedMenuPaths([]);
        }
    };

    // Menu permission handlers
    const toggleMenuPath = (path) => {
        setSelectedMenuPaths(prev => {
            if (prev.includes(path)) {
                return prev.filter(p => p !== path);
            } else {
                return [...prev, path];
            }
        });
    };

    const selectAllMenus = () => {
        setSelectedMenuPaths(allMenuItems.map(item => item.path));
    };

    const clearAllMenus = () => {
        setSelectedMenuPaths([]);
    };

    const handleImageChange = (event) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];

            // Validate file before saving to state
            const validation = authUtils.validateImage(file);

            if (!validation.isValid) {
                toast.error(validation.errors.join('\n'));
                return;
            }

            // Save file to state
            setCurrentEmployee(prev => ({
                ...prev,
                'FILE_IMAGE': file
            }));

            // Show preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setSelectedImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const validateEmployee = (employee) => {
        const errors = [];
        if (!employee['Họ và Tên']) errors.push('Họ và tên không được để trống');
        if (!employee['username']) errors.push('Tên đăng nhập không được để trống');
        if (!isEditMode && !employee['password']) errors.push('Mật khẩu không được để trống');
        if (!employee['Email']) errors.push('Email không được để trống');

        // Basic email validation
        if (employee['Email'] && !/\S+@\S+\.\S+/.test(employee['Email'])) {
            errors.push('Email không hợp lệ');
        }

        return errors;
    };

    // Save employee
    const handleSaveEmployee = async () => {
        if (isSubmitting || !isAdmin) {
            if (!isAdmin) {
                toast.error('Bạn không có quyền thực hiện chức năng này!');
            }
            return;
        }

        try {
            setIsSubmitting(true);
            const errors = validateEmployee(currentEmployee);
            if (errors.length > 0) {
                toast.error(errors.join('\n'));
                setIsSubmitting(false);
                return;
            }

            // Create a copy of employee to process
            let employeeToSave = { ...currentEmployee };

            // Lưu quyền xem menu (nếu không phải Admin)
            if (employeeToSave['Phân quyền'] !== 'Admin') {
                employeeToSave['Quyền xem'] = selectedMenuPaths.join(',');
            } else {
                employeeToSave['Quyền xem'] = ''; // Admin không cần quyền xem cụ thể
            }

            // If there's a new image file, upload to server
            if (employeeToSave['FILE_IMAGE']) {
                try {
                    const uploadResult = await authUtils.uploadImage(employeeToSave['FILE_IMAGE']);
                    if (uploadResult && uploadResult.success && uploadResult.url) {
                        // Update image URL from upload result
                        employeeToSave['Image'] = uploadResult.url;
                    } else {
                        throw new Error("Không thể lấy URL ảnh");
                    }
                } catch (error) {
                    toast.error('Lỗi khi tải ảnh: ' + error.message);
                    setIsSubmitting(false);
                    return;
                }
            }

            // Remove FILE_IMAGE field before sending to API
            delete employeeToSave['FILE_IMAGE'];

            if (isEditMode) {
                // Edit existing employee
                await authUtils.apiRequest('DSNV', 'Edit', {
                    "Rows": [employeeToSave]
                });
                toast.success('Cập nhật thông tin nhân viên thành công!');
            } else {
                // Create new employee
                const existingEmployees = await authUtils.apiRequest('DSNV', 'Find', {});

                // Check if username already exists
                const exists = existingEmployees.some(emp =>
                    emp['username'].toLowerCase() === employeeToSave['username'].toLowerCase()
                );

                if (exists) {
                    toast.error('Tên đăng nhập này đã tồn tại!');
                    setIsSubmitting(false);
                    return;
                }

                await authUtils.apiRequest('DSNV', 'Add', {
                    "Rows": [employeeToSave]
                });
                toast.success('Thêm nhân viên mới thành công!');
            }

            await fetchEmployees();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving employee:', error);
            toast.error('Có lỗi xảy ra: ' + (error.message || 'Không thể lưu thông tin nhân viên'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Delete handlers
    const handleOpenDeleteConfirmation = (employee) => {
        if (!isAdmin) {
            toast.error('Bạn không có quyền xóa nhân viên!');
            return;
        }
        setEmployeeToDelete(employee);
        setShowDeleteConfirmation(true);
    };

    const handleCloseDeleteConfirmation = () => {
        setShowDeleteConfirmation(false);
        setEmployeeToDelete(null);
    };

    const handleDeleteEmployee = async () => {
        if (!employeeToDelete) return;

        try {
            await authUtils.apiRequest('DSNV', 'Delete', {
                "Rows": [{ "username": employeeToDelete['username'] }]
            });
            toast.success('Xóa nhân viên thành công!');
            await fetchEmployees();
            handleCloseDeleteConfirmation();
        } catch (error) {
            console.error('Error deleting employee:', error);
            toast.error('Có lỗi xảy ra khi xóa nhân viên');
        }
    };

    // Sorting
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortedItems = useCallback(() => {
        const sortableItems = [...employees];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                const keyA = a[sortConfig.key] || '';
                const keyB = b[sortConfig.key] || '';

                if (keyA < keyB) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (keyA > keyB) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [employees, sortConfig]);

    // Get unique departments and positions for filtering
    const departments = ['TẤT CẢ', ...new Set(employees.map(emp => emp['Phòng']).filter(Boolean))];
    const positions = ['TẤT CẢ', ...new Set(employees.map(emp => emp['Chức vụ']).filter(Boolean))];

    // Filtering
    const filteredItems = getSortedItems().filter(employee => {
        const matchesSearch =
            (employee['Họ và Tên']?.toLowerCase().includes(search.toLowerCase()) ||
                employee['username']?.toLowerCase().includes(search.toLowerCase()) ||
                employee['Email']?.toLowerCase().includes(search.toLowerCase()));

        const matchesDepartment = filterDepartment === 'TẤT CẢ' || employee['Phòng'] === filterDepartment;
        const matchesPosition = filterPosition === 'TẤT CẢ' || employee['Chức vụ'] === filterPosition;

        return matchesSearch && matchesDepartment && matchesPosition;
    });

    // Get sort direction icon
    const getSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return null;
        }
        return sortConfig.direction === 'ascending' ? '↑' : '↓';
    };

    // Toggle password visibility
    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleRefresh = async () => {
        toast.info('Đang tải lại dữ liệu...');
        await fetchEmployees();
        toast.success('Đã tải lại dữ liệu thành công!');
    };

    return (
        <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
            <div className="mx-auto">
                <div className="bg-white rounded-xl shadow-lg p-3 md:p-4 mb-4 border border-gray-100">
                    {/* Header Section */}
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-3 gap-3">
                        <div className="flex items-center gap-2">
                            <div className="p-2 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg shadow-md">
                                <Users className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl md:text-2xl font-bold text-gray-800">Quản Lý Người Dùng</h1>
                                <p className="text-xs md:text-sm text-gray-500 mt-0.5">Quản lý tài khoản và phân quyền hệ thống</p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={handleRefresh}
                                className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
                                title="Tải lại dữ liệu"
                            >
                                <RefreshCw className="w-4 h-4" />
                                <span className="hidden sm:inline">Làm mới</span>
                            </button>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm ${showFilters
                                        ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                <Filter className="w-4 h-4" />
                                <span className="hidden sm:inline">{showFilters ? "Ẩn bộ lọc" : "Bộ lọc"}</span>
                            </button>
                            {isAdmin && (
                                <button
                                    onClick={() => handleOpenModal()}
                                    className="px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-lg hover:from-indigo-600 hover:to-indigo-700 flex items-center gap-1.5 transition-all shadow-md hover:shadow-lg text-sm font-medium"
                                >
                                    <Plus className="w-4 h-4" />
                                    <span>Thêm người dùng</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Search and Filter Section */}
                    <div className="mb-3 space-y-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Tìm kiếm theo tên, username hoặc email..."
                                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>

                        {showFilters && (
                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 animate-fadeIn">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {/* Department filter */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-2">Lọc theo phòng ban:</label>
                                        <div className="flex flex-wrap gap-2">
                                            {departments.map((dept, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => setFilterDepartment(dept)}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filterDepartment === dept
                                                        ? 'bg-indigo-100 text-indigo-800 border border-indigo-200 shadow-sm'
                                                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                                                        }`}
                                                >
                                                    {dept}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Position filter */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-2">Lọc theo chức vụ:</label>
                                        <div className="flex flex-wrap gap-2">
                                            {positions.map((pos, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => setFilterPosition(pos)}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filterPosition === pos
                                                        ? 'bg-blue-100 text-blue-800 border border-blue-200 shadow-sm'
                                                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                                                        }`}
                                                >
                                                    {pos}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Statistics cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-3">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg border-l-4 border-blue-500 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-2 mb-1">
                                <Users className="w-4 h-4 text-blue-600" />
                                <p className="text-xs font-medium text-blue-700">Tổng người dùng</p>
                            </div>
                            <p className="text-2xl font-bold text-blue-900">{employees.length}</p>
                        </div>

                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-3 rounded-lg border-l-4 border-purple-500 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-2 mb-1">
                                <Shield className="w-4 h-4 text-purple-600" />
                                <p className="text-xs font-medium text-purple-700">Quản trị viên</p>
                            </div>
                            <p className="text-2xl font-bold text-purple-900">
                                {employees.filter(emp => emp['Phân quyền'] === 'Admin').length}
                            </p>
                            <p className="text-xs text-purple-600 mt-0.5">
                                {((employees.filter(emp => emp['Phân quyền'] === 'Admin').length / employees.length) * 100 || 0).toFixed(1)}%
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-green-50 to-green-100 p-3 rounded-lg border-l-4 border-green-500 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-2 mb-1">
                                <UserCheck className="w-4 h-4 text-green-600" />
                                <p className="text-xs font-medium text-green-700">Quản lý</p>
                            </div>
                            <p className="text-2xl font-bold text-green-900">
                                {employees.filter(emp => emp['Phân quyền'] === 'Quản lý').length}
                            </p>
                            <p className="text-xs text-green-600 mt-0.5">
                                {((employees.filter(emp => emp['Phân quyền'] === 'Quản lý').length / employees.length) * 100 || 0).toFixed(1)}%
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-3 rounded-lg border-l-4 border-orange-500 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-2 mb-1">
                                <UserCheck className="w-4 h-4 text-orange-600" />
                                <p className="text-xs font-medium text-orange-700">Người dùng</p>
                            </div>
                            <p className="text-2xl font-bold text-orange-900">
                                {employees.filter(emp => emp['Phân quyền'] === 'User').length}
                            </p>
                            <p className="text-xs text-orange-600 mt-0.5">
                                {((employees.filter(emp => emp['Phân quyền'] === 'User').length / employees.length) * 100 || 0).toFixed(1)}%
                            </p>
                        </div>
                    </div>

                    {/* Table Section */}
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col"
                                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('Họ và Tên')}>
                                        Họ và Tên {getSortIcon('Họ và Tên')}
                                    </th>
                                    <th scope="col"
                                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('Chức vụ')}>
                                        Chức Vụ {getSortIcon('Chức vụ')}
                                    </th>
                                    <th scope="col"
                                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('Phòng')}>
                                        Phòng {getSortIcon('Phòng')}
                                    </th>
                                    <th scope="col"
                                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('username')}>
                                        Username {getSortIcon('username')}
                                    </th>
                                    <th scope="col"
                                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('Phân quyền')}>
                                        Phân Quyền {getSortIcon('Phân quyền')}
                                    </th>
                                    <th scope="col"
                                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                        onClick={() => requestSort('Email')}>
                                        Email {getSortIcon('Email')}
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Hình ảnh
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                        Thao tác
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredItems.length > 0 ? (
                                    filteredItems.map((employee, index) => (
                                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {employee['Họ và Tên']}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {employee['Chức vụ'] || '—'}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {employee['Phòng'] || '—'}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {employee['username']}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${employee['Phân quyền'] === 'Admin'
                                                    ? 'bg-purple-100 text-purple-800'
                                                    : 'bg-blue-100 text-blue-800'
                                                    }`}>
                                                    {employee['Phân quyền']}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                                <div className="max-w-xs truncate">{employee['Email'] || '—'}</div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {employee['Image'] ? (
                                                    <div className="w-10 h-10 relative">
                                                        <img
                                                            src={employee['Image']}
                                                            alt={employee['Họ và Tên']}
                                                            className="w-full h-full object-cover rounded-full border border-gray-200"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full border border-gray-200">
                                                        <Image className="w-5 h-5 text-gray-400" />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                                <div className="flex justify-center space-x-2">
                                                    {isAdmin && (
                                                        <>
                                                            <button
                                                                onClick={() => handleOpenModal(employee)}
                                                                className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded-full hover:bg-indigo-50"
                                                                title="Sửa thông tin nhân viên"
                                                            >
                                                                <Edit className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleOpenDeleteConfirmation(employee)}
                                                                className="text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50"
                                                                title="Xóa nhân viên"
                                                            >
                                                                <Trash className="h-4 w-4" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="px-4 py-6 text-center text-sm text-gray-500">
                                            Không tìm thấy nhân viên nào phù hợp với tiêu chí tìm kiếm
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Add/Edit Employee Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                   <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
                        {/* Header - Fixed */}
                        <div className="flex justify-between items-center border-b border-gray-200 p-6 flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <div className="p-2 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg shadow-sm">
                                    <Users className="w-5 h-5 text-white" />
                                </div>
                                <h2 className="text-xl font-bold text-gray-800">
                                    {isEditMode ? 'Cập nhật thông tin người dùng' : 'Thêm người dùng mới'}
                                </h2>
                            </div>
                            <button
                                onClick={handleCloseModal}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Content - Scrollable */}
                        <div className="overflow-y-auto flex-1 p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Left Column */}
                            <div className="space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Họ và tên <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={currentEmployee['Họ và Tên']}
                                        onChange={(e) => handleInputChange('Họ và Tên', e.target.value)}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Nhập họ và tên"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Chức vụ
                                    </label>
                                    <input
                                        type="text"
                                        value={currentEmployee['Chức vụ']}
                                        onChange={(e) => handleInputChange('Chức vụ', e.target.value)}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Nhập chức vụ"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Phòng
                                    </label>
                                    <input
                                        type="text"
                                        value={currentEmployee['Phòng']}
                                        onChange={(e) => handleInputChange('Phòng', e.target.value)}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Nhập phòng ban"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        value={currentEmployee['Email']}
                                        onChange={(e) => handleInputChange('Email', e.target.value)}
                                        className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Nhập địa chỉ email"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Phân quyền <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex space-x-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="role"
                                                value="Admin"
                                                checked={currentEmployee['Phân quyền'] === 'Admin'}
                                                onChange={(e) => handleInputChange('Phân quyền', e.target.value)}
                                                className="mr-2 h-4 w-4 text-indigo-600"
                                            />
                                            <span className="text-gray-800">Admin</span>
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="role"
                                                value="Quản lý"
                                                checked={currentEmployee['Phân quyền'] === 'Quản lý'}
                                                onChange={(e) => handleInputChange('Phân quyền', e.target.value)}
                                                className="mr-2 h-4 w-4 text-indigo-600"
                                            />
                                            <span className="text-gray-800">Quản lý</span>
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="role"
                                                value="User"
                                                checked={currentEmployee['Phân quyền'] === 'User'}
                                                onChange={(e) => handleInputChange('Phân quyền', e.target.value)}
                                                className="mr-2 h-4 w-4 text-indigo-600"
                                            />
                                            <span className="text-gray-800">User</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Tên đăng nhập <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={currentEmployee['username']}
                                        onChange={(e) => handleInputChange('username', e.target.value)}
                                        className={`p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500 ${isEditMode ? 'bg-gray-100' : ''}`}
                                        placeholder="Nhập tên đăng nhập"
                                        readOnly={isEditMode}
                                        required
                                    />
                                    {isEditMode && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Tên đăng nhập không thể thay đổi sau khi đã tạo.
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Mật khẩu {!isEditMode && <span className="text-red-500">*</span>}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword && isAdmin ? "text" : "password"}
                                            value={currentEmployee['password']}
                                            onChange={(e) => handleInputChange('password', e.target.value)}
                                            className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500 pr-10"
                                            placeholder={isEditMode ? "Nhập để thay đổi mật khẩu" : "Nhập mật khẩu"}
                                            required={!isEditMode}
                                            readOnly={!isAdmin}
                                        />
                                        {isAdmin && (
                                            <button
                                                type="button"
                                                onClick={togglePasswordVisibility}
                                                className="absolute inset-y-0 right-0 flex items-center pr-3"
                                            >
                                                {showPassword ? (
                                                    <EyeOff className="h-5 w-5 text-gray-400" />
                                                ) : (
                                                    <Eye className="h-5 w-5 text-gray-400" />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                    {isEditMode && isAdmin && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Để trống nếu không muốn thay đổi mật khẩu.
                                        </p>
                                    )}
                                    {!isAdmin && (
                                        <p className="text-xs text-red-500 mt-1">
                                            Chỉ Admin mới có quyền xem và sửa mật khẩu.
                                        </p>
                                    )}
                                </div>

                                <div className="mt-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Hình ảnh
                                    </label>
                                    <div
                                        ref={dropZoneRef}
                                        onDragEnter={handleDragEnter}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        className={`p-4 border-2 border-dashed rounded-lg flex flex-col items-center justify-center bg-gray-50 transition-all cursor-pointer hover:bg-gray-100 ${isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`}
                                    >
                                        <div className="w-full flex items-center justify-center mb-3">
                                            <div className="w-24 h-24 border rounded-full flex items-center justify-center bg-white overflow-hidden">
                                                {selectedImage ? (
                                                    <img
                                                        src={selectedImage}
                                                        alt="Employee preview"
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <Image className="h-10 w-10 text-gray-300" />
                                                )}
                                            </div>
                                        </div>

                                        <div className="text-center">
                                            <Upload className="h-5 w-5 mx-auto text-gray-400 mb-1" />
                                            <p className="text-sm text-gray-600 mb-1">
                                                {isDragging ? 'Thả ảnh vào đây' : 'Kéo và thả ảnh vào đây hoặc'}
                                            </p>
                                            <label className="cursor-pointer px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors shadow-sm inline-flex items-center">
                                                <Upload className="h-4 w-4 mr-2" />
                                                Chọn ảnh
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    accept="image/*"
                                                    onChange={handleImageChange}
                                                />
                                            </label>
                                        </div>

                                        <p className="text-xs text-gray-500 mt-3">
                                            Hình ảnh nên có kích thước vuông, tối đa 5MB.
                                        </p>
                                    </div>

                                    {selectedImage && (
                                        <div className="mt-2 flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedImage(null);
                                                    handleInputChange('Image', '');
                                                }}
                                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors shadow-sm flex items-center"
                                            >
                                                <X className="h-4 w-4 mr-1" />
                                                Xóa ảnh
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Menu Permissions Section - Chỉ hiển thị khi không phải Admin */}
                        {currentEmployee['Phân quyền'] !== 'Admin' && (
                            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Quyền xem menu <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={selectAllMenus}
                                            className="px-3 py-1 text-xs bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
                                        >
                                            Chọn tất cả
                                        </button>
                                        <button
                                            type="button"
                                            onClick={clearAllMenus}
                                            className="px-3 py-1 text-xs bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                                        >
                                            Bỏ chọn
                                        </button>
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mb-3">
                                    Chọn các menu mà {currentEmployee['Phân quyền']} này có quyền truy cập
                                </p>
                                <div className="max-h-64 overflow-y-auto space-y-3 pr-2">
                                    {/* Group by category */}
                                    {['Quản lý kho', 'Quản lý năng suất', 'Quản lý công lương', 'Quản lý bán hàng', 'Quản lý lô', 'Chức năng khác'].map(category => {
                                        const categoryItems = allMenuItems.filter(item => item.category === category);
                                        if (categoryItems.length === 0) return null;

                                        return (
                                            <div key={category} className="bg-white p-3 rounded-lg border border-gray-200">
                                                <h4 className="text-sm font-semibold text-gray-700 mb-2">{category}</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                    {categoryItems.map(item => (
                                                        <label key={item.path} className="flex items-center space-x-2 text-sm hover:bg-gray-50 p-2 rounded cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedMenuPaths.includes(item.path)}
                                                                onChange={() => toggleMenuPath(item.path)}
                                                                className="h-4 w-4 text-indigo-600 rounded focus:ring-indigo-500"
                                                            />
                                                            <span className="text-gray-700">{item.title}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    Đã chọn: <span className="font-semibold text-indigo-600">{selectedMenuPaths.length}</span> / {allMenuItems.length} menu
                                </p>
                            </div>
                        )}
                        </div>

                        {/* Footer - Fixed */}
                        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 flex-shrink-0 bg-gray-50">
                            <button
                                onClick={handleCloseModal}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all shadow-sm hover:shadow flex items-center"
                                disabled={isSubmitting}
                            >
                                <X className="h-4 w-4 mr-1" />
                                Hủy
                            </button>
                            <button
                                onClick={handleSaveEmployee}
                                disabled={isSubmitting}
                                className={`px-5 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${isSubmitting
                                    ? 'bg-indigo-400 cursor-not-allowed text-white'
                                    : 'bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg'
                                    }`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Đang lưu...
                                    </>
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Lưu người dùng
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirmation && employeeToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <h2 className="text-xl font-bold text-gray-800">Xác nhận xóa</h2>
                            <button
                                onClick={handleCloseDeleteConfirmation}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="space-y-5">
                            <div className="bg-gradient-to-br from-red-50 to-red-100 border-l-4 border-red-500 rounded-lg p-4 shadow-sm">
                                <p className="text-red-800 font-medium">
                                    Bạn có chắc chắn muốn xóa người dùng <span className="font-bold">{employeeToDelete['Họ và Tên']}</span>?
                                </p>
                                <p className="text-sm text-red-600 mt-2">
                                    Hành động này không thể hoàn tác và sẽ xóa tất cả thông tin của người dùng này.
                                </p>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    onClick={handleCloseDeleteConfirmation}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all shadow-sm hover:shadow"
                                >
                                    Hủy
                                </button>
                                <button
                                    onClick={handleDeleteEmployee}
                                    className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 flex items-center gap-2 transition-all shadow-md hover:shadow-lg font-medium"
                                >
                                    <Trash className="h-4 w-4" />
                                    Xóa người dùng
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Container */}
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
};

export default DSNVManagement;